import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      ...routeMeta("/faq"),
      { title: "Frequently Asked Questions | CCOB And Company PA" },
      { name: "description", content: "Answers to common questions about accounting, taxes, audits, payroll, virtual consultations, deposits, and turnaround times." },
      { property: "og:title", content: "FAQ — CCOB And Company PA" },
      { property: "og:description", content: "Common questions about CPA services in Winter Garden, FL." },
    ],
    links: canonicalLinks("/faq"),
  }),
  component: FaqPage,
});

const groups = [
  {
    cat: "Accounting",
    items: [
      { q: "Do you handle bookkeeping for small businesses?", a: "Yes. We offer monthly bookkeeping for sole proprietors, LLCs, and small corporations across Central Florida." },
      { q: "Can you produce financial statements for my bank?", a: "Absolutely — we prepare professional, bank-ready monthly, quarterly, and annual statements." },
    ],
  },
  {
    cat: "Tax Filing",
    items: [
      { q: "When should I bring my tax documents?", a: "As early as possible after January 31. Early submissions get priority scheduling." },
      { q: "Do you e-file?", a: "Yes — all eligible federal and state returns are filed electronically with confirmations." },
    ],
  },
  {
    cat: "IRS Representation",
    items: [
      { q: "I received an IRS letter — can you help?", a: "Yes. Bring or email us the notice and we'll review it, explain the next step, and represent you if needed." },
      { q: "Can you handle audits?", a: "Yes. We provide full audit support and IRS representation as a licensed CPA firm." },
    ],
  },
  {
    cat: "Virtual Consultations",
    items: [
      { q: "Do you meet virtually?", a: "We offer in-person, video, and phone consultations depending on your service needs." },
    ],
  },
  {
    cat: "Payroll",
    items: [
      { q: "Do you process payroll?", a: "Yes — we handle weekly, biweekly, and monthly payroll along with related tax filings." },
    ],
  },
  {
    cat: "Audit Preparation",
    items: [
      { q: "How can I prepare for an audit?", a: "We'll walk you through documentation, internal controls, and reconciliations to ensure you're audit-ready." },
    ],
  },
  {
    cat: "Deposits & Payment Process",
    items: [
      { q: "Are deposits required?", a: "Some services — particularly tax preparation and representation — require a deposit before work begins." },
    ],
  },
  {
    cat: "Turnaround Times",
    items: [
      { q: "How long does a typical project take?", a: "Most individual returns: 5–10 business days. Bookkeeping engagements onboard within 1–2 weeks." },
    ],
  },
];

function FaqPage() {
  const [open, setOpen] = useState<string | null>("Accounting-0");
  return (
    <>
      <PageHero eyebrow="FAQ" title="Answers to the Questions We Hear Most" subtitle="Don't see yours? Reach out and we'll respond promptly." image={img.heroFaq} />
      <section className="py-24">
        <div className="container-x max-w-4xl mb-12">
          <h2 className="font-display text-3xl text-primary">Answers before we begin</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">These answers cover how consultations, document sharing, fees, timing, and ongoing CPA support generally work. Every financial situation is different, so we confirm the scope and next steps with you before an engagement begins.</p>
        </div>
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <h2 className="font-display text-2xl text-primary">Categories</h2>
            <div className="gold-line mt-4" />
            <ul className="mt-6 space-y-2 text-sm">
              {groups.map((g) => (
                <li key={g.cat}><a href={`#${g.cat}`} className="text-muted-foreground hover:text-gold">{g.cat}</a></li>
              ))}
            </ul>
          </aside>
          <div className="lg:col-span-9 space-y-12">
            {groups.map((g) => (
              <motion.div key={g.cat} id={g.cat} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                <h3 className="font-display text-2xl text-primary">{g.cat}</h3>
                <div className="mt-4 divide-y divide-border border border-border rounded-2xl bg-card overflow-hidden">
                  {g.items.map((it, i) => {
                    const id = `${g.cat}-${i}`;
                    const isOpen = open === id;
                    return (
                      <button key={id} onClick={() => setOpen(isOpen ? null : id)} className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-secondary/40 transition">
                        <div>
                          <div className="font-medium text-primary">{it.q}</div>
                          {isOpen && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.a}</p>}
                        </div>
                        <ChevronDown className={`h-5 w-5 text-gold transition-transform shrink-0 mt-1 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
